import { Injectable, inject, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { firstValueFrom } from 'rxjs';
import { CumplimientoRepository } from 'src/app/domain/service/cumplimiento/cumplimiento-repository';
import { MunicipioRepository } from 'src/app/domain/service/municipio/municipio-repository';
import { PoblacionRepository } from 'src/app/domain/service/transporte/poblacion-repository';
import {
  ProgramacionPendiente,
  CumplimientoRequest,
  CumplimientoResponse,
  DatosActaRequest
} from 'src/app/domain/model/cumplimiento/cumplimiento';
import {
  ESTADO_CITA,
  MANEJO_TIPO_OTRO,
  MAX_CORREO_CONTACTO,
  MAX_NOMBRE_CONTACTO,
  MAX_VALOR_TRANSPORTE_ADICIONAL,
  NO_VALUE,
  TIPO_CITA,
  YES_VALUE
} from 'src/app/common/constants-ejb';
import { environment } from 'src/environments/environment';
import { avisarPostPersistencia } from 'src/app/common/aviso-post-persistencia';
import { redondearCantidad } from 'src/app/common/cantidad.utils';
import { calcularDiasHabiles, dateToString } from 'src/app/common/date-utils';
import { formatCurrency } from 'src/app/common/format-utils';
import {
  esContactoObligatorio,
  requiredTrimmed
} from 'src/app/common/contacto-obligatorio.utils';
import { sanitizeText, sanitizeTextarea } from 'src/app/common/input-utils';
import {
  isValidEmail,
  isValidEmailContactoLegacy,
  sanitizeNombreContacto
} from 'src/app/common/input-utils';
import { abrirAlertaTemprana } from 'src/app/common/alerta-temprana.utils';
import {
  esTipoCitaCampo,
  esTipoCitaInforme
} from 'src/app/common/tipo-cita.utils';
import { ContactoAdicional } from '../components/contactos-adicionales-dialog/contactos-adicionales-dialog.component';
import { ArchivoInforme } from '../components/seccion-enviar-informe/seccion-enviar-informe.component';
import { DatosActaFormValue } from '../components/seccion-acta/seccion-acta.component';
import { ContactoPersonaRepository } from 'src/app/domain/service/persona/contacto-persona-repository';

export interface Municipio {
  cdsura: string;
  cdmunicipio: string;
  nombre: string;
  departamento?: string;
}

const EMPTY_STRING = '';
const FLUJO_NO_PROGRAMADA = 'NO_PROGRAMADA';
const TIPO_INFORME_PRELIMINAR = 'P';
const MIN_EJECUCION_VALUE = 0.01;
const JUSTIFICACION_CLASIFICACION_REQUERIDA = '3';
const MSG_AVISO = 'Aviso';
const MSG_NO_PROGRAMACION_SELECCIONADA = 'No hay una programación seleccionada';
const ACTA_TIPO_ASESOR = 'ASESOR';
const ACTA_TIPO_CAPACITACI = 'CAPACITACI';
const ACTA_LEGACY_SI = 'Si';
const ACTA_LEGACY_NO = 'No';
const ACTA_LEGACY_ATENCION_VIRTUAL = 'Virtual';
const ACTA_LEGACY_ATENCION_PRESENCIAL = 'Presencial';
const ACTA_ERROR_DESCRIPCION_SERVICIO =
  'Descripción del Servicio Prestado (Acta)';
const ACTA_ERROR_ANEXOS_REQUERIDOS = 'Anexos (Acta)';
const ACTA_ERROR_ANEXOS_REQUERIDOS_POR_RELACION =
  'Si diligencia Relación de Documentación, debe adjuntar al menos un anexo.';
const ACTA_ERROR_RELACION_DOCUMENTACION = 'Relación de Documentación (Acta)';
const ACTA_ERROR_ENVIO_NOTIFICACION = 'Envío de Notificación (Acta)';

/**
 * Facade de formulario de cumplimiento.
 * Maneja el estado y lógica del formulario de registro/edición.
 * Split desde CumplimientosProgramadosFacade para cumplir límite ~500 líneas.
 */
@Injectable()
export class CumplimientoFormFacade {
  private readonly http = inject(HttpClient);
  private readonly messageService = inject(MessageService);
  private readonly cumplimientoRepo = inject(CumplimientoRepository);
  private readonly municipioRepo = inject(MunicipioRepository);
  private readonly poblacionRepo = inject(PoblacionRepository);
  private readonly contactoPersonaRepo = inject(ContactoPersonaRepository);

  // === Estado del Formulario ===
  readonly dialogFormVisible = signal(false);
  readonly formLoading = signal(false);
  readonly saldoActual = signal(0);
  readonly requiereJustificacion = signal(false);

  // === Configuración Cumplimiento Tardío ===
  readonly limitDaysProgram = signal<number>(0);
  readonly festivos = signal<string[]>([]);
  readonly loadingConfigTardio = signal(false);
  /** true si la última carga de límite de días y festivos falló (la tardanza no se puede validar). */
  readonly configTardioError = signal(false);
  /**
   * Tope del valor de transporte adicional.
   * Legacy: maxTteAdic = GeneralParameters.MAX_TTE_ADIC_CMPL, acotado por maxLength=6.
   */
  readonly maxTransporteAdicional = signal<number>(
    MAX_VALOR_TRANSPORTE_ADICIONAL
  );

  // === Estado Transporte ===
  readonly municipiosSugeridos = signal<Municipio[]>([]);
  readonly municipioOrigen = signal<Municipio | null>(null);
  readonly municipioDestino = signal<Municipio | null>(null);

  // === Estado Contactos Adicionales ===
  readonly contactosAdicionales = signal<
    { dsnombre: string; dsemail: string }[]
  >([]);
  readonly mostrarContactosDialog = signal(false);

  // === Estado Acta 360 (HU1132419) ===
  readonly actaGenerando = signal(false);
  readonly actaError = signal<string | null>(null);
  readonly acta360IdGenerado = signal<number | null>(null);
  readonly mostrarBotonReintentar = signal(false);
  /** Valor actual del panel de acta (emitido por seccion-acta) */
  datosActaActual: DatosActaFormValue | null = null;
  /** Número de asistentes enviado al guardar, reutilizado en reintento si aplica */
  ultimoNumeroAsistentesActa: number | null = null;

  // === Catálogos formulario ===
  readonly justificaciones = signal<{ label: string; value: number }[]>([
    { label: 'Reprogramación por cliente', value: 1 },
    { label: 'Reprogramación por consultor', value: 2 },
    { label: 'Incapacidad del consultor', value: 3 },
    { label: 'Fuerza mayor', value: 4 },
    { label: 'Otro', value: 99 }
  ]);

  // === Programación activa ===
  readonly selectedProgramacion = signal<ProgramacionPendiente | null>(null);

  // === Computed: Tipo de Cita ===
  readonly esTipoCampo = computed(() =>
    esTipoCitaCampo(this.selectedProgramacion())
  );

  readonly esTipoInforme = computed(() =>
    esTipoCitaInforme(this.selectedProgramacion())
  );

  readonly mostrarSeccionTransporte = computed(() => this.esTipoCampo());

  /** Municipio de origen precargado desde la programación */
  readonly municipioOrigenInicial = computed(() => {
    const prog = this.selectedProgramacion();
    if (!prog?.cdmunicipioOrigen && !prog?.cdsuraOrigen) return null;
    return {
      cdmunicipio: prog.cdmunicipioOrigen || '',
      cdsura: prog.cdsuraOrigen || '',
      dsmunicipio: prog.dsmunicipioOrigen || prog.cdmunicipioOrigen || ''
    };
  });

  /** Municipio de entrega precargado desde la programación */
  readonly municipioEntregaInicial = computed(() => {
    const prog = this.selectedProgramacion();
    if (!prog?.cdmunicipioEntrega && !prog?.cdsuraEntrega) return null;
    return {
      cdmunicipio: prog.cdmunicipioEntrega || '',
      cdsura: prog.cdsuraEntrega || '',
      dsmunicipio: prog.dsmunicipioEntrega || prog.cdmunicipioEntrega || ''
    };
  });

  // =========================================================================
  // MÉTODOS PÚBLICOS - Formulario
  // =========================================================================

  /**
   * Abrir formulario para registrar cumplimiento.
   * Legacy: CreateOrUpdateCumplimientoActivity
   */
  async abrirFormularioCumplimiento(
    programacion: ProgramacionPendiente
  ): Promise<void> {
    this.selectedProgramacion.set(programacion);
    this.saldoActual.set(programacion.saldoPorEjecutar || 0);
    this.dialogFormVisible.set(true);
    this.municipioOrigen.set(null);
    this.municipioDestino.set(null);
    this.contactosAdicionales.set([]);
    this.mostrarContactosDialog.set(false);
    await this.cargarConfiguracionTardio();
    this.verificarCumplimientoTardio(new Date());
    this.consultarSaldoActualizado(programacion.nmconsecOrdenServDetalle);
  }

  /** Cerrar formulario */
  cerrarFormulario(): void {
    this.dialogFormVisible.set(false);
    this.selectedProgramacion.set(null);
    this.contactosAdicionales.set([]);
    this.mostrarContactosDialog.set(false);
    this.actaGenerando.set(false);
    this.actaError.set(null);
    this.acta360IdGenerado.set(null);
    this.mostrarBotonReintentar.set(false);
    this.datosActaActual = null;
    this.ultimoNumeroAsistentesActa = null;
  }

  /**
   * Abrir URL de Alerta Temprana.
   * Legacy: CreateOrUpdateCumplimientoViewImpl.java → saNuevoRp.onClickNewRp()
   */
  abrirUrlAlertaTemprana(): void {
    void abrirAlertaTemprana(this.selectedProgramacion(), this.messageService);
  }

  /**
   * Ver historial de cumplimientos.
   * Legacy: saHistProg → showModalHistProg()
   */
  verHistorialCumplimientos(): void {
    const prog = this.selectedProgramacion();
    if (!prog) {
      this.messageService.add({
        severity: 'warn',
        summary: MSG_AVISO,
        detail: MSG_NO_PROGRAMACION_SELECCIONADA
      });
      return;
    }
    this.messageService.add({
      severity: 'info',
      summary: 'Historial de Cumplimientos',
      detail: `Funcionalidad de historial para orden ${prog.nmconsecutivoManualOrden} pendiente de implementar`,
      life: 5000
    });
  }

  /** Guardar contactos adicionales. Legacy: AdicionarContactosActivity.buildListDto() */
  guardarContactosAdicionales(
    contactos: { dsnombre: string; dsemail: string }[]
  ): void {
    this.contactosAdicionales.set(contactos);
    this.mostrarContactosDialog.set(false);
  }

  abrirContactosDialog(): void {
    this.mostrarContactosDialog.set(true);
  }

  // =========================================================================
  // LÓGICA DE FORMULARIO (extraída de form-logic.service para cumplimiento estricto)
  // =========================================================================

  initForm(fb: FormBuilder): FormGroup {
    return fb.group({
      fechaCumplimiento: [new Date(), Validators.required],
      fechaProgramacion: [null],
      cantidadEjecutada: [
        null,
        [Validators.required, Validators.min(MIN_EJECUCION_VALUE)]
      ],
      cdtipoCita: [null],
      horaCita: [''],
      duracion: [''],
      numeroAsistentes: [null],
      nombreContacto: [''],
      correoContacto: ['', Validators.email],
      snatencionVirtual: [NO_VALUE],
      snpagoTransporteEmpleado: [NO_VALUE],
      cdmunicipioOrigen: [''],
      cdmunicipioEntrega: [''],
      cdpoblacion: [''],
      ptvalorTransporte: [null],
      snrequiereTransporteAdicional: [NO_VALUE],
      ptvalorTransporteAdicional: [0],
      dsobservacionesTransporteAdicional: [''],
      observaciones: [''],
      observacionNotificacion: [''],
      observacionesInforme: [''],
      recomendacionesInforme: [''],
      snidentificaRiesgo: [NO_VALUE],
      cdjustificacion: [null],
      observacionesJustificacion: [''],
      archivoInforme: [null],
      tipoInforme: [TIPO_INFORME_PRELIMINAR],
      snmailAprobacion: [false]
    });
  }

  validarFormulario(params: {
    form: FormGroup;
    esTipoInformeActual: boolean;
    esTipoCampoActual: boolean;
    isNoProgramada: boolean;
    asistentesRequeridos: boolean;
    requiereJustificacion: boolean;
    mostrarHoraCita: boolean;
    mostrarDuracionNoProgramada: boolean;
    archivoInformeData: ArchivoInforme | null;
    isCumpComplementarios?: boolean;
    datosActa?: DatosActaFormValue | null;
    feprogramacion?: Date | string | null;
    cantidadProgramada?: number | null;
    saldoPorEjecutar?: number | null;
    esSinOc?: boolean;
  }): string[] {
    const {
      form,
      esTipoInformeActual,
      esTipoCampoActual,
      isNoProgramada,
      asistentesRequeridos,
      requiereJustificacion,
      mostrarHoraCita,
      mostrarDuracionNoProgramada,
      archivoInformeData,
      isCumpComplementarios,
      datosActa,
      feprogramacion,
      cantidadProgramada,
      saldoPorEjecutar,
      esSinOc
    } = params;
    const errores: string[] = [];
    const f = form.getRawValue();

    if (!f['fechaCumplimiento']) errores.push('Fecha de Cumplimiento');

    // Legacy: CreateOrUpdateCumplimientoViewImpl.validarFechaPosterior() (línea 1582).
    // Solo valida la fecha de cumplimiento (la que ingresa el usuario). La fecha de
    // programación viene de la cita ya creada y puede ser legítimamente futura
    // (cierre anticipado de cita programada).
    const hoy = new Date();
    hoy.setHours(23, 59, 59, 999);
    const fechaCumpl = f['fechaCumplimiento']
      ? new Date(f['fechaCumplimiento'])
      : null;
    if (fechaCumpl && fechaCumpl.getTime() > hoy.getTime()) {
      errores.push(
        'La Fecha de Cumplimiento no puede ser posterior a la fecha actual'
      );
    }

    if (
      !esTipoInformeActual &&
      (!f['cantidadEjecutada'] || f['cantidadEjecutada'] < MIN_EJECUCION_VALUE)
    ) {
      errores.push('Cantidad Ejecutada');
    }

    // Legacy: validarCantidadParaProgramacion / validarCantidadParaOrden.
    // Programada: cantidad <= nmcantidad_programada (mensaje toolTipCantidadEjecutadasInvalida).
    // No programada: cantidad <= saldoEjecutar (toolTipCantidadEjecutadasInvalidariesgos).
    // Sin OC: skip (la OC se crea en el momento, no hay saldo previo).
    const cantEj = redondearCantidad(Number(f['cantidadEjecutada'] ?? 0));
    if (cantEj > 0 && !esSinOc) {
      if (
        !isNoProgramada &&
        cantidadProgramada != null &&
        cantEj > redondearCantidad(Number(cantidadProgramada))
      ) {
        errores.push(
          'La cantidad ejecutada NO debe ser mayor que la cantidad programada'
        );
      } else if (
        isNoProgramada &&
        saldoPorEjecutar != null &&
        cantEj > redondearCantidad(Number(saldoPorEjecutar))
      ) {
        errores.push(
          'La cantidad pedida NO debe ser mayor que el Saldo por Ejecutar'
        );
      }
    }

    if (isNoProgramada) {
      if (!f['cdtipoCita']) errores.push('Tipo de Cita');
      if (!f['fechaProgramacion']) errores.push('Fecha de Programación');
      if (mostrarHoraCita && !f['horaCita']) errores.push('Hora Cita');
      if (
        mostrarDuracionNoProgramada &&
        (!f['duracion'] || Number(f['duracion']) <= 0)
      ) {
        errores.push('Duración');
      }
    }
    if (asistentesRequeridos) {
      const numAsistentes = f['numeroAsistentes'];
      const numStr =
        numAsistentes !== null && numAsistentes !== undefined
          ? String(numAsistentes)
          : '';
      if (Number(numAsistentes ?? 0) <= 0) {
        errores.push(
          'La cantidad en el número de asistentes debe ser mayor a cero (0)'
        );
      } else if (numStr.length > 5) {
        // Legacy onClickGrabar: "La cantidad de dígitos permitida para el número de asistentes es hasta 5"
        errores.push(
          'La cantidad de dígitos permitida para el número de asistentes es hasta 5'
        );
      }
    }
    // Legacy CreateOrUpdateCumplimientoViewImpl: :2627-2628 setRequired(visible && !isCumpComplementarios)
    // y tbCorreoContacto.setRegex(Constants.REGEX_EMAILS). Desviación aprobada (requerimiento de QA):
    // en Campo Complementario el contacto también es obligatorio (ver esContactoObligatorio).
    const contactoObligatorio = esContactoObligatorio({
      esTipoCampo: esTipoCampoActual
    });
    if (contactoObligatorio) {
      if (!f['nombreContacto']?.trim())
        errores.push('Nombre del Contacto');
      if (!f['correoContacto']?.trim())
        errores.push('Correo del Contacto');
    }
    if (esTipoCampoActual) {
      if (
        f['correoContacto']?.trim() &&
        !isValidEmailContactoLegacy(f['correoContacto'])
      ) {
        errores.push('Correo del Contacto (formato inválido)');
      }
      // Legacy: tbNombreContacto maxLength=50 / tbCorreoContacto maxLength=100.
      if ((f['nombreContacto']?.trim().length ?? 0) > MAX_NOMBRE_CONTACTO) {
        errores.push(
          `Nombre del Contacto (máximo ${MAX_NOMBRE_CONTACTO} caracteres)`
        );
      }
      if ((f['correoContacto']?.trim().length ?? 0) > MAX_CORREO_CONTACTO) {
        errores.push(
          `Correo del Contacto (máximo ${MAX_CORREO_CONTACTO} caracteres)`
        );
      }
    }

    if (esTipoCampoActual) {
      // Legacy: setRequiredFields() marca tbMunicipioOrigen/Entrega required;
      // mostrarOcultarTransporte los libera cuando el panel se oculta (informe o virtual).
      const esVirtual =
        String(f['snatencionVirtual'] || NO_VALUE).toUpperCase() === YES_VALUE;
      if (!esVirtual) {
        if (!f['cdmunicipioOrigen']?.toString().trim())
          errores.push('Municipio Origen');
        if (!f['cdmunicipioEntrega']?.toString().trim())
          errores.push('Municipio Entrega');
      }

      const requiereTransporteAdicional =
        String(f['snrequiereTransporteAdicional'] || NO_VALUE).toUpperCase() ===
        YES_VALUE;
      const valorTransporteAdicional = Number(
        f['ptvalorTransporteAdicional'] ?? 0
      );

      if (requiereTransporteAdicional && valorTransporteAdicional < 0) {
        errores.push('Valor Transporte Adicional');
      }

      // Legacy: KeyPressHandler de tbValorTransporteAdicional (valor > maxTteAdic).
      const maxTransporteAdicional = this.maxTransporteAdicional();
      if (
        requiereTransporteAdicional &&
        valorTransporteAdicional > maxTransporteAdicional
      ) {
        errores.push(
          `Valor Transporte Adicional (máximo ${formatCurrency(maxTransporteAdicional)})`
        );
      }

      if (
        requiereTransporteAdicional &&
        valorTransporteAdicional > 0 &&
        !f['dsobservacionesTransporteAdicional']?.trim()
      ) {
        errores.push('Observaciones Transporte Adicional');
      }
    }

    if (esTipoInformeActual && !isCumpComplementarios) {
      if (!archivoInformeData) errores.push('Archivo del Informe');
      if (!f['tipoInforme']) errores.push('Tipo de Informe');
      // Legacy: taRecomendaciones.setRequired(visible) con visible=isTipoCampo → no obligatorio en Tipo Informe.
    }
    if (esTipoInformeActual && isCumpComplementarios) {
      if (!archivoInformeData) errores.push('Archivo del Informe');
    }
    // Legacy: showInfoCamposDeCampo → taObservaciones visible pero NO required para complementarios
    // Para CAMPO_COMPLEMENTARIO (42): seccion-observaciones es visible
    // Para INFORME_COMPLEMENTARIO (43): seccion-observaciones NO es visible (esTipoInformeActual=true)
    if (isCumpComplementarios && !esTipoInformeActual) {
      if (!f['observaciones']?.trim()) errores.push('Observaciones');
      if (!f['recomendacionesInforme']?.trim()) errores.push('Recomendaciones');
    }
    if (requiereJustificacion) {
      if (!f['cdjustificacion']) errores.push('Tipo Justificación');
      if (!f['observacionesJustificacion']?.trim())
        errores.push('Observaciones');
    }
    // Legacy: CreateOrUpdateCumplimientoViewImpl.java — campos acta obligatorios cuando panel visible
    // taObservaciones.setRequired(visible), taRecomendaciones.setRequired(visible)
    if (datosActa != null) {
      if (!datosActa.descripcionServicioPrestado?.trim()) {
        errores.push(ACTA_ERROR_DESCRIPCION_SERVICIO);
      }
      const tieneAnexos = (datosActa.anexos?.length ?? 0) > 0;
      const tieneRelacion = !!datosActa.relacionDocumentacion?.trim();

      // Capacitación: al menos 1 anexo (legacy validacion_campo_anexos).
      if (datosActa.tipoActa === ACTA_TIPO_CAPACITACI && !tieneAnexos) {
        errores.push(ACTA_ERROR_ANEXOS_REQUERIDOS);
      }
      // Asesoría: regla bidireccional anexos ↔ relación de documentación.
      // - Si hay anexos sin relación → exigir relación (legacy validacion_campo_relacion_documentacion_con_anexos).
      // - Si hay relación sin anexos → exigir anexos (regla adicional al legacy).
      if (datosActa.tipoActa === ACTA_TIPO_ASESOR) {
        if (tieneAnexos && !tieneRelacion) {
          errores.push(ACTA_ERROR_RELACION_DOCUMENTACION);
        }
        if (tieneRelacion && !tieneAnexos) {
          errores.push(ACTA_ERROR_ANEXOS_REQUERIDOS_POR_RELACION);
        }
      }
      if (!datosActa.envioNotificacion?.trim()) {
        errores.push(ACTA_ERROR_ENVIO_NOTIFICACION);
      }
      // El servicio 360 rechaza HTTP 417 si la fecha de prestación es futura;
      // validamos antes para evitar la llamada y mostrar mensaje claro.
      const fechaPrestacion = this.resolverFechaPrestacionParaActa(
        f['fechaCumplimiento'],
        feprogramacion
      );
      if (fechaPrestacion && fechaPrestacion.getTime() > hoy.getTime()) {
        errores.push(
          'La fecha de prestación del servicio no debe ser mayor a la fecha actual'
        );
      }
    }
    return errores;
  }

  /**
   * Determina la fecha de prestación a validar antes de generar el acta.
   * Toma la fechaCumplimiento si está; si no, recurre a feprogramacion de la cita.
   */
  private resolverFechaPrestacionParaActa(
    fechaCumplimiento: Date | string | null | undefined,
    feprogramacion: Date | string | null | undefined
  ): Date | null {
    const candidato = fechaCumplimiento ?? feprogramacion ?? null;
    if (!candidato) return null;
    const fecha = candidato instanceof Date ? candidato : new Date(candidato);
    return isNaN(fecha.getTime()) ? null : fecha;
  }

  actualizarValidadores(params: {
    form: FormGroup;
    esTipoInformeActual: boolean;
    esTipoCampoActual: boolean;
    isNoProgramada: boolean;
    requiereJustificacion: boolean;
    mostrarHoraCita: boolean;
    mostrarDuracionNoProgramada: boolean;
    dstipomanejo: string;
    isCumpComplementarios?: boolean;
  }): void {
    const {
      form,
      esTipoInformeActual,
      esTipoCampoActual,
      isNoProgramada,
      requiereJustificacion,
      mostrarHoraCita,
      mostrarDuracionNoProgramada,
      isCumpComplementarios
    } = params;

    // Desviación aprobada del legacy (aceb02c1, Victor Lopez, 2026-07-03): en el legacy
    // (CreateOrUpdateCumplimientoViewImpl.setEnableCantidadEjecutada) un informe cuya unidad de medida no se
    // maneja por tiempo (DSTIPOMANEJO distinto de MANEJO_TIPO_TIEMPO) dejaba la cantidad deshabilitada en 0 y no
    // consumía saldo. Por decisión funcional la cantidad es editable y obligatoria en todo informe.
    if (esTipoInformeActual) {
      form.get('cantidadEjecutada')?.enable();
      form
        .get('cantidadEjecutada')
        ?.setValidators([
          Validators.required,
          Validators.min(MIN_EJECUCION_VALUE)
        ]);

      // Regla funcional: conservar valor existente en informe; si no existe, iniciar vacío.
      const cantidadEjecutadaControl = form.get('cantidadEjecutada');
      const cantidadEjecutadaActual = cantidadEjecutadaControl?.value;
      if (
        cantidadEjecutadaActual === undefined ||
        cantidadEjecutadaActual === ''
      ) {
        cantidadEjecutadaControl?.setValue(null, { emitEvent: false });
      }
    } else {
      form.get('cantidadEjecutada')?.enable();
      form
        .get('cantidadEjecutada')
        ?.setValidators([
          Validators.required,
          Validators.min(MIN_EJECUCION_VALUE)
        ]);
    }

    // Misma regla que validarFormulario: sin campos en rojo que luego no bloquean el guardado.
    const contactoObligatorio = esContactoObligatorio({
      esTipoCampo: esTipoCampoActual
    });
    const maxNombre = Validators.maxLength(MAX_NOMBRE_CONTACTO);
    const maxCorreo = Validators.maxLength(MAX_CORREO_CONTACTO);
    if (contactoObligatorio) {
      form.get('nombreContacto')?.setValidators([requiredTrimmed, maxNombre]);
      form
        .get('correoContacto')
        ?.setValidators([requiredTrimmed, Validators.email, maxCorreo]);
    } else {
      form.get('nombreContacto')?.setValidators(maxNombre);
      form.get('correoContacto')?.setValidators([Validators.email, maxCorreo]);
    }

    if (esTipoInformeActual || isCumpComplementarios) {
      // Legacy: Recomendaciones NO es obligatorio en Tipo Informe puro (visible=isTipoCampo=false).
      // Solo es obligatorio para CAMPO_COMPLEMENTARIO (isCumpComplementarios && !esTipoInformeActual).
      if (isCumpComplementarios && !esTipoInformeActual) {
        form.get('recomendacionesInforme')?.setValidators(Validators.required);
      } else {
        form.get('recomendacionesInforme')?.clearValidators();
      }
      if (esTipoInformeActual && !isCumpComplementarios) {
        form.get('tipoInforme')?.setValidators(Validators.required);
      } else {
        form.get('tipoInforme')?.clearValidators();
      }
    } else {
      form.get('recomendacionesInforme')?.clearValidators();
      form.get('tipoInforme')?.clearValidators();
    }

    // Legacy: showInfoCamposDeCampo → observaciones visible para CAMPO_COMPLEMENTARIO, no para INFORME_COMPLEMENTARIO
    if (isCumpComplementarios && !esTipoInformeActual) {
      form.get('observaciones')?.setValidators(Validators.required);
    } else {
      form.get('observaciones')?.clearValidators();
    }

    if (isNoProgramada) {
      form.get('cdtipoCita')?.setValidators(Validators.required);
      if (mostrarHoraCita) {
        form.get('horaCita')?.setValidators(Validators.required);
      } else {
        form.get('horaCita')?.clearValidators();
      }
      if (mostrarDuracionNoProgramada) {
        form
          .get('duracion')
          ?.setValidators([
            Validators.required,
            Validators.min(MIN_EJECUCION_VALUE)
          ]);
      } else {
        form.get('duracion')?.clearValidators();
      }
    } else {
      form.get('cdtipoCita')?.clearValidators();
      form.get('horaCita')?.clearValidators();
      form.get('duracion')?.clearValidators();
    }

    if (requiereJustificacion) {
      form.get('cdjustificacion')?.setValidators(Validators.required);
      form
        .get('observacionesJustificacion')
        ?.setValidators(Validators.required);
    } else {
      form.get('cdjustificacion')?.clearValidators();
      form.get('observacionesJustificacion')?.clearValidators();
    }

    [
      'cantidadEjecutada',
      'nombreContacto',
      'correoContacto',
      'recomendacionesInforme',
      'tipoInforme',
      'cdjustificacion',
      'observacionesJustificacion',
      'cdtipoCita',
      'horaCita',
      'duracion',
      'numeroAsistentes',
      'observaciones'
    ].forEach((ctrl) =>
      form.get(ctrl)?.updateValueAndValidity({ emitEvent: false })
    );
  }

  /**
   * Legacy (CreateOrUpdateCumplimientoActivity.crearNuevoContacto + ViewImpl.obtenerContacto): en el
   * cumplimiento sin programación, el contacto de la programación es el seleccionado en la lupa o,
   * si no hay, el contacto de la orden con el correo digitado; si no existe, se crea.
   *
   * @returns consecutivo del contacto, o null si no se pudo resolver (no bloquea el guardado, como en
   *          el legacy, donde la creación era asíncrona y no detenía la grabación)
   */
  async resolverContactoProgramacion(
    nmconsecOrden: number,
    nombre: string,
    correo: string
  ): Promise<number | null> {
    try {
      const contacto = await this.contactoPersonaRepo.resolverDesdeOrden(nmconsecOrden, {
        cdpersona: 0,
        dsnombre: sanitizeNombreContacto(nombre).trim(),
        dsemail: (correo ?? '').trim()
      });
      return contacto?.nmconsecutivo ?? null;
    } catch (error) {
      if (!environment.production) {
        console.error('No fue posible resolver el contacto de la programación', error);
      }
      return null;
    }
  }

  /**
   * Variante de {@link resolverContactoProgramacion} para el cumplimiento sin OC: la orden aún no existe,
   * pero SP_CREAR_BENEF_CT ya dejó la persona y el centro de trabajo. Legacy crearNuevoContacto trabaja con
   * el DTO de la orden (beneficiario + nmconsec_benef_ct), no con un consecutivo de orden.
   *
   * @returns consecutivo del contacto, o null si no se pudo resolver (no bloquea el guardado)
   */
  async resolverContactoProgramacionPorCentroTrabajo(
    cdpersona: number,
    nmconsecCentroTrabajo: number,
    nombre: string,
    correo: string
  ): Promise<number | null> {
    try {
      const contacto = await this.contactoPersonaRepo.resolverDesdeCentroTrabajo(
        cdpersona,
        nmconsecCentroTrabajo,
        {
          cdpersona,
          dsnombre: sanitizeNombreContacto(nombre).trim(),
          dsemail: (correo ?? '').trim()
        }
      );
      return contacto?.nmconsecutivo ?? null;
    } catch (error) {
      if (!environment.production) {
        console.error('No fue posible resolver el contacto de la programación', error);
      }
      return null;
    }
  }

  buildCumplimientoRequest(params: {
    form: FormGroup;
    prog: ProgramacionPendiente;
    esTipoInformeActual: boolean;
    esTipoCampoActual: boolean;
    isNoProgramada: boolean;
    esAtencionVirtual: boolean;
    isCumpComplementarios?: boolean;
    requiereJustificacion: boolean;
    contactosAdicionales: ContactoAdicional[];
    archivoInformeData: ArchivoInforme | null;
    /** Sin OC de línea de atención con tipo de cita de campo: el correo al contacto lleva observación. */
    incluirObservacionNotificacion?: boolean;
  }): CumplimientoRequest {
    const {
      form,
      prog,
      esTipoInformeActual,
      esTipoCampoActual,
      isNoProgramada,
      esAtencionVirtual,
      isCumpComplementarios,
      requiereJustificacion,
      contactosAdicionales,
      archivoInformeData,
      incluirObservacionNotificacion
    } = params;
    const f = form.getRawValue();

    return {
      fechaCumplimiento: dateToString(f['fechaCumplimiento']),
      cantidadEjecutada: redondearCantidad(Number(f['cantidadEjecutada'] ?? 0)),
      observaciones: esTipoInformeActual
        ? f['observacionesInforme']
        : f['observaciones'],
      recomendaciones:
        esTipoInformeActual || isCumpComplementarios
          ? f['recomendacionesInforme']
          : undefined,
      // Defensa en profundidad: el backend rechaza HTML/script con @SafeText (HTTP 400)
      ...(incluirObservacionNotificacion && {
        observacionNotificacion: sanitizeTextarea(f['observacionNotificacion']).trim()
      }),
      horaCita: f['horaCita'],
      duracion: f['duracion']
        ? redondearCantidad(Number(f['duracion']))
        : undefined,
      ...(isNoProgramada && {
        nmconsecOrdenServDetalle: prog.nmconsecOrdenServDetalle,
        cdtipoCita: f['cdtipoCita'],
        fechaProgramacion: f['fechaProgramacion']
          ? f['fechaProgramacion'] instanceof Date
            ? dateToString(f['fechaProgramacion'])
            : f['fechaProgramacion']
          : undefined,
        cantidadProgramadaSinOc: this.resolverCantidadProgramadaNoProgramada(
          prog,
          f['cantidadEjecutada']
        )
      }),
      ...(!isNoProgramada && { nmconsecutivoProgramacion: prog.nmconsecutivo }),
      ...(esTipoCampoActual && {
        numeroAsistentes: f['numeroAsistentes'],
        // El backend valida PERSON_NAME: sin tildes, ñ ni caracteres especiales (legacy cleanString).
        nombreContacto: sanitizeNombreContacto(f['nombreContacto']).trim(),
        correoContacto: f['correoContacto'],
        snatencionVirtual: f['snatencionVirtual'],
        snidentificaRiesgo: f['snidentificaRiesgo']
      }),
      cdmunicipioOrigen: f['cdmunicipioOrigen'] || undefined,
      cdmunicipioEntrega: f['cdmunicipioEntrega'] || undefined,
      ...(esTipoCampoActual &&
        !esAtencionVirtual && {
          snpagoTransporteEmpleado: f['snpagoTransporteEmpleado'],
          cdpoblacion: f['cdpoblacion'] || undefined,
          ptvalorTransporte: f['ptvalorTransporte'] || undefined,
          snrequiereTransporteAdicional: f['snrequiereTransporteAdicional'],
          ptvalorTransporteAdicional:
            f['snrequiereTransporteAdicional'] === YES_VALUE
              ? (f['ptvalorTransporteAdicional'] ?? 0)
              : 0,
          dsobservacionesTransporteAdicional:
            f['dsobservacionesTransporteAdicional'] || undefined
        }),
      ...(requiereJustificacion && {
        cdjustificacion: f['cdjustificacion'],
        observacionesJustificacion: f['observacionesJustificacion']
      }),
      ...(contactosAdicionales.length > 0 && {
        contactosAdicionales: contactosAdicionales.map(c => ({
          ...c,
          dsnombre: sanitizeNombreContacto(c.dsnombre).trim()
        }))
      }),
      ...(esTipoInformeActual &&
        archivoInformeData && {
          archivoNombre: archivoInformeData.archivoNombre,
          archivoContenido: archivoInformeData.archivoContenido,
          archivoContentType: archivoInformeData.archivoContentType,
          tipoInforme: f['tipoInforme'],
          snmailAprobacion: f['snmailAprobacion']
        }),
      // === Acta 360 (HU1132419) — solo si hay datosActa del panel ===
      ...(this.datosActaActual &&
        esTipoCampoActual && {
          datosActa: this.buildDatosActaRequest(
            this.datosActaActual,
            undefined,
            f['numeroAsistentes'],
            {
              snidentificaRiesgo: f['snidentificaRiesgo'],
              snatencionVirtual: f['snatencionVirtual']
            }
          ),
          tipoActa: this.datosActaActual.tipoActa
        })
    };
  }

  private resolverCantidadProgramadaNoProgramada(
    prog: ProgramacionPendiente,
    cantidadEjecutada: unknown
  ): number | undefined {
    const cantidadEjecutadaNormalizada = redondearCantidad(
      Number(cantidadEjecutada ?? 0)
    );
    const cantidadProgramada = redondearCantidad(
      Number(prog.nmcantidadProgramada ?? 0)
    );
    const esSinOc = prog.cumplimientoSinOc === true;

    if (esSinOc && cantidadProgramada > 0) {
      return cantidadProgramada;
    }
    if (cantidadEjecutadaNormalizada > 0) {
      return cantidadEjecutadaNormalizada;
    }
    return cantidadProgramada > 0 ? cantidadProgramada : undefined;
  }

  resetFormValues(form: FormGroup): void {
    form.reset({
      fechaCumplimiento: new Date(),
      cantidadEjecutada: null,
      cdtipoCita: null,
      horaCita: '',
      duracion: '',
      numeroAsistentes: null,
      nombreContacto: '',
      correoContacto: '',
      snatencionVirtual: NO_VALUE,
      snpagoTransporteEmpleado: NO_VALUE,
      cdmunicipioOrigen: '',
      cdmunicipioEntrega: '',
      cdpoblacion: '',
      ptvalorTransporte: null,
      snrequiereTransporteAdicional: NO_VALUE,
      ptvalorTransporteAdicional: 0,
      dsobservacionesTransporteAdicional: '',
      observaciones: '',
      observacionNotificacion: '',
      observacionesInforme: '',
      recomendacionesInforme: '',
      snidentificaRiesgo: NO_VALUE,
      cdjustificacion: null,
      observacionesJustificacion: '',
      archivoInforme: null,
      tipoInforme: TIPO_INFORME_PRELIMINAR,
      snmailAprobacion: false
    });
  }

  isNoProgramada(flujo: string): boolean {
    return flujo === FLUJO_NO_PROGRAMADA;
  }

  esTipoCampoActual(
    flujo: string,
    esTipoCampo: boolean,
    tipoCitaValue: any
  ): boolean {
    if (flujo !== FLUJO_NO_PROGRAMADA) return esTipoCampo;
    const tipo = Number(tipoCitaValue);
    return tipo === TIPO_CITA.CAMPO || tipo === TIPO_CITA.CAMPO_COMPLEMENTARIO;
  }

  esTipoInformeActual(
    flujo: string,
    esTipoInforme: boolean,
    tipoCitaValue: any
  ): boolean {
    if (flujo !== FLUJO_NO_PROGRAMADA) return esTipoInforme;
    const tipo = Number(tipoCitaValue);
    return (
      tipo === TIPO_CITA.INFORME || tipo === TIPO_CITA.INFORME_COMPLEMENTARIO
    );
  }

  mostrarHoraCita(flujo: string, programacion: any): boolean {
    if (flujo !== FLUJO_NO_PROGRAMADA) return true;
    return (
      (programacion?.snprogramacionAutomatica ?? NO_VALUE) === NO_VALUE &&
      (programacion?.snocautomatica ?? NO_VALUE) === YES_VALUE
    );
  }

  mostrarDuracionNoProgramada(flujo: string, programacion: any): boolean {
    return (
      flujo === FLUJO_NO_PROGRAMADA &&
      (programacion?.dstipomanejo || EMPTY_STRING).toUpperCase() ===
        MANEJO_TIPO_OTRO
    );
  }

  mostrarAsistentes(
    flujo: string,
    esTipoCampo: boolean,
    tipoCitaValue: any
  ): boolean {
    return this.esTipoCampoActual(flujo, esTipoCampo, tipoCitaValue);
  }

  asistentesRequeridos(
    flujo: string,
    esTipoCampo: boolean,
    tipoCitaValue: any,
    cdclasificacion: string
  ): boolean {
    return this.esTipoCampoActual(flujo, esTipoCampo, tipoCitaValue);
  }

  /** Guardar cumplimiento y retornar si fue exitoso */
  async guardarCumplimiento(request: CumplimientoRequest): Promise<boolean> {
    this.formLoading.set(true);
    this.actaError.set(null);
    this.actaGenerando.set(false);
    this.mostrarBotonReintentar.set(false);
    this.ultimoNumeroAsistentesActa =
      request.datosActa?.numeroAsistentes ?? null;
    try {
      const response: CumplimientoResponse =
        await this.cumplimientoRepo.registrarCumplimiento(request);
      this.formLoading.set(false);
      avisarPostPersistencia(this.messageService, response);

      // Manejar resultado de generación de acta (HU1132419)
      if (response?.asociarActa) {
        if (response.idActa360 != null) {
          this.acta360IdGenerado.set(response.idActa360);
          this.messageService.add({
            severity: 'success',
            summary: 'Cumplimiento registrado',
            detail: 'Cumplimiento registrado correctamente.'
          });
          this.messageService.add({
            severity: 'success',
            summary: 'Acta asociada',
            detail: `El acta ${response.idActa360} fue generada y asociada al cumplimiento.`
          });
        } else {
          // Generación falló — mostrar error + botón reintentar.
          this.mostrarBotonReintentar.set(true);
          this.actaError.set(
            'El cumplimiento fue guardado, pero ocurrió un error al generar el acta. Puede reintentarlo.'
          );
          this.messageService.add({
            severity: 'warn',
            summary: 'Advertencia',
            detail:
              'Cumplimiento guardado. No se pudo generar el acta automáticamente.',
            life: 8000
          });
        }
      } else {
        this.messageService.add({
          severity: 'success',
          summary: 'Éxito',
          detail: 'Cumplimiento registrado correctamente'
        });
      }

      if (!response?.asociarActa || response.idActa360 != null) {
        this.cerrarFormulario();
      }
      return true;
    } catch (err: any) {
      this.formLoading.set(false);
      const detalleValidacion = this.extraerDetalleValidacionArchivo(err);
      const errorMsg =
        detalleValidacion ??
        (err.error?.message ||
          err.error?.detail ||
          err.message ||
          'Error al registrar el cumplimiento');
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: errorMsg
      });
      return false;
    }
  }

  /**
   * Reintenta la generación del acta 360 para un cumplimiento ya guardado.
   * HU1132419 — Botón "Generar Nuevamente Acta"
   */
  async reintentarGenerarActa(
    nmconsecutivoCumplimiento: number
  ): Promise<void> {
    if (!this.datosActaActual) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Aviso',
        detail: 'No hay datos del acta para reintentar.'
      });
      return;
    }
    this.actaGenerando.set(true);
    this.actaError.set(null);
    try {
      const datosActa = this.buildDatosActaRequest(
        this.datosActaActual,
        nmconsecutivoCumplimiento,
        this.ultimoNumeroAsistentesActa
      );
      const result = await this.cumplimientoRepo.reintentarGenerarActa(
        nmconsecutivoCumplimiento,
        datosActa
      );
      this.acta360IdGenerado.set(result.idActa360);
      this.mostrarBotonReintentar.set(false);
      this.actaGenerando.set(false);
      this.messageService.add({
        severity: 'success',
        summary: 'Éxito',
        detail: `Acta generada exitosamente (ID: ${result.idActa360})`
      });
      this.cerrarFormulario();
    } catch (err: any) {
      this.actaGenerando.set(false);
      const detalleValidacion = this.extraerDetalleValidacionArchivo(err);
      const msg =
        detalleValidacion ??
        (err.error?.message ||
          err.message ||
          'Error al reintentar la generación del acta');
      this.actaError.set(msg);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: msg
      });
    }
  }

  /**
   * Si el backend respondió con un 400 por validación de anexos
   * (`GenerarActaRestAdapter.validarAnexos` → `IllegalArgumentException`),
   * devuelve el `detail` con el nombre del archivo afectado. Caso contrario null.
   *
   * Legacy: el flujo de generación de acta en GWT mostraba el nombre del archivo
   * que falló la validación junto con el aviso "No se pudo generar el acta".
   */
  private extraerDetalleValidacionArchivo(err: any): string | null {
    if (err?.status !== 400) {
      return null;
    }
    const code = err?.error?.code;
    const detail = err?.error?.detail;
    if (
      code === 'validation.error' &&
      typeof detail === 'string' &&
      detail.trim().length > 0
    ) {
      return detail;
    }
    return null;
  }

  /**
   * Convierte el valor del panel de acta al DTO que espera el backend.
   * Se llama desde buildCumplimientoRequest() y reintentarGenerarActa().
   */
  buildDatosActaRequest(
    datosActa: DatosActaFormValue,
    nmconsecutivoCumplimiento?: number,
    numeroAsistentesOverride?: number | null,
    contexto?: {
      snidentificaRiesgo?: string | null;
      snatencionVirtual?: string | null;
    }
  ): DatosActaRequest {
    const prog = this.selectedProgramacion();
    const snidentificaRiesgo = contexto?.snidentificaRiesgo ?? prog?.snidentificaRiesgo;
    const snatencionVirtual = contexto?.snatencionVirtual ?? prog?.snatencionVirtual;
    const totalAsistentesLista = datosActa.asistentes?.length ?? 0;
    const numeroAsistentesForm =
      numeroAsistentesOverride != null
        ? Number(numeroAsistentesOverride)
        : null;
    const manualValido =
      numeroAsistentesForm != null &&
      !Number.isNaN(numeroAsistentesForm) &&
      numeroAsistentesForm > 0;
    // Regla backend:
    // - Asesoría: numeroAsistentes DEBE coincidir con asistentes.length → enviar siempre list.size.
    // - Capacitación: no lleva lista (debe ir vacía) → enviar el valor manual del formulario.
    const esAsesor = datosActa.tipoActa === ACTA_TIPO_ASESOR;
    const numeroAsistentes = esAsesor
      ? totalAsistentesLista
      : manualValido
        ? (numeroAsistentesForm as number)
        : 0;
    // Skill seguridad-formularios: defense in depth — sanitizar el payload antes
    // de salir del navegador, independientemente del (input) en los componentes.
    return {
      descripcionServicioPrestado: sanitizeTextarea(
        datosActa.descripcionServicioPrestado
      ),
      compromisosEmpresaAfiliada: sanitizeTextarea(
        datosActa.compromisosEmpresaAfiliada
      ),
      compromisosSura: sanitizeTextarea(datosActa.compromisosSura),
      relacionDocumentacion: sanitizeTextarea(datosActa.relacionDocumentacion),
      fuente: 'GIROP',
      idCumplimiento: nmconsecutivoCumplimiento
        ? String(nmconsecutivoCumplimiento)
        : undefined,
      requierePlanAccion: snidentificaRiesgo === 'S' ? ACTA_LEGACY_SI : ACTA_LEGACY_NO,
      canalAtencion: snatencionVirtual === 'S' ? ACTA_LEGACY_ATENCION_VIRTUAL : ACTA_LEGACY_ATENCION_PRESENCIAL,
      envioNotificacion: datosActa.envioNotificacion,
      numeroAsistentes,
      asistentes: (datosActa.asistentes ?? []).map((a) => ({
        nombre: sanitizeText(a.nombre),
        cargo: sanitizeText(a.cargo)
      })),
      anexos: datosActa.anexos
    };
  }

  // =========================================================================
  // CUMPLIMIENTO TARDÍO - Legacy: CreateOrUpdateCumplimientoActivity
  // =========================================================================

  private normalizarLimitDaysProgram(valor: unknown): number {
    const normalizado = Number.parseInt(String(valor ?? '').trim(), 10);
    if (Number.isNaN(normalizado) || normalizado < 0) {
      return 0;
    }
    return normalizado;
  }

  /** Sin parámetro (o inválido) queda el tope estructural de 6 dígitos del legacy. */
  private normalizarMaxTransporteAdicional(valor: unknown): number {
    if (valor == null || String(valor).trim() === '') {
      return MAX_VALOR_TRANSPORTE_ADICIONAL;
    }
    const normalizado = Number(String(valor).trim());
    if (!Number.isFinite(normalizado) || normalizado < 0) {
      return MAX_VALOR_TRANSPORTE_ADICIONAL;
    }
    return Math.min(normalizado, MAX_VALOR_TRANSPORTE_ADICIONAL);
  }

  /**
   * Carga la configuración para validación de cumplimiento tardío.
   * Legacy: loadParameters() + loadParametersHoliDays(). Los festivos ya no vienen del parámetro
   * HOLIDAYS: los entrega agenda-ms desde T094_DIAS_FESTIVOS (misma fuente que valida el backend).
   */
  async cargarConfiguracionTardio(): Promise<void> {
    if (this.loadingConfigTardio()) return;
    this.loadingConfigTardio.set(true);
    this.configTardioError.set(false);
    try {
      const configCumplimiento =
        await this.cumplimientoRepo.obtenerConfiguracionCumplimiento();
      this.limitDaysProgram.set(
        this.normalizarLimitDaysProgram(configCumplimiento?.limitDaysProgram)
      );
      this.maxTransporteAdicional.set(
        this.normalizarMaxTransporteAdicional(
          configCumplimiento?.maxTransporteAdicional
        )
      );
      const configAgenda = await firstValueFrom(
        this.http.get<{ festivos: string[] }>(
          `${environment.backend.baseUrl}/agenda/configuracion`
        )
      );
      if (configAgenda?.festivos) this.festivos.set(configAgenda.festivos);
    } catch {
      // No se asumen valores por defecto (límite 0 y sin festivos): el formulario no debe
      // decidir la tardanza sin la configuración real. onGuardar reintenta y, si sigue
      // fallando, no guarda.
      this.configTardioError.set(true);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail:
          'No se pudo cargar la configuración (límite de días y festivos) para validar si el cumplimiento es tardío. Intente nuevamente.',
        life: 5000
      });
    } finally {
      this.loadingConfigTardio.set(false);
    }
  }

  /**
   * Verificar si cumplimiento es tardío.
   * Legacy: CreateOrUpdateCumplimientoViewImpl.cumplimientoTardio()
   */
  verificarCumplimientoTardio(fechaCumplimiento: Date): void {
    const programacion = this.selectedProgramacion();
    if (!programacion?.feprogramacion) return;
    const fechaProg = new Date(programacion.feprogramacion);
    const diasHabiles = calcularDiasHabiles(
      fechaProg,
      fechaCumplimiento,
      this.festivos()
    );
    this.requiereJustificacion.set(diasHabiles > this.limitDaysProgram());
  }

  // =========================================================================
  // HELPERS
  // =========================================================================

  /** Mapea tipo de cita a severity de PrimeNG Tag */
  getTipoSeverity(
    nmtipoCita: number | string | undefined
  ): 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' {
    if (!nmtipoCita) return 'secondary';
    const tipo =
      typeof nmtipoCita === 'string' ? parseInt(nmtipoCita, 10) : nmtipoCita;
    switch (tipo) {
      case TIPO_CITA.CAMPO:
        return 'info';
      case TIPO_CITA.INFORME:
        return 'success';
      case TIPO_CITA.CAMPO_COMPLEMENTARIO:
        return 'warn';
      case TIPO_CITA.INFORME_COMPLEMENTARIO:
        return 'warn';
      default:
        return 'secondary';
    }
  }

  /** Mapea código de estado a severity de PrimeNG Tag */
  getEstadoSeverity(
    cdestado: string | undefined
  ): 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' {
    switch (cdestado) {
      case ESTADO_CITA.PROGRAMADA:
        return 'info';
      case ESTADO_CITA.EJECUTADA:
        return 'success';
      case ESTADO_CITA.CANCELADA:
        return 'danger';
      case ESTADO_CITA.RESERVADA:
        return 'warn';
      default:
        return 'secondary';
    }
  }

  // =========================================================================
  // CALLBACKS para formulario-cumplimiento (evita inyectar repos en dumb component)
  // =========================================================================

  /** Callback para obtener tarifa de transporte. Provisto al formulario-cumplimiento. */
  readonly obtenerTarifaCallback = async (params: {
    cdsuraOrigen: string;
    cdsuraDestino: string;
    cdpoblacion?: string;
    nmconsecutivoOficina?: number;
    nmconsecutivoCliente?: number;
    nmconsecutivoAsesor?: number;
  }): Promise<number | null> => {
    try {
      const result = await firstValueFrom(
        this.poblacionRepo.obtenerTarifa(
          params.cdsuraOrigen,
          params.cdsuraDestino,
          params.cdpoblacion,
          params.nmconsecutivoOficina,
          params.nmconsecutivoCliente,
          params.nmconsecutivoAsesor
        )
      );
      return result?.ptcosto ?? null;
    } catch {
      return null;
    }
  };

  /** Callback para obtener municipio por código DANE. Provisto al formulario-cumplimiento. */
  readonly obtenerMunicipioCallback = async (
    codigo: string
  ): Promise<{
    cdmunicipio: string;
    cdsura: string;
    dsmunicipio: string;
  } | null> => {
    try {
      const m = await this.municipioRepo.getByCodigo(codigo);
      return m
        ? {
            cdmunicipio: m.cdmunicipio || codigo,
            cdsura: m.cdsura || '',
            dsmunicipio: m.dsmunicipio || ''
          }
        : null;
    } catch {
      return null;
    }
  };

  private async consultarSaldoActualizado(
    nmconsecOrdenServDetalle: number
  ): Promise<void> {
    if (!nmconsecOrdenServDetalle) return;
    try {
      const response = await this.cumplimientoRepo.obtenerSaldoOrden(
        nmconsecOrdenServDetalle
      );
      this.saldoActual.set(response.saldoPorEjecutar);
    } catch {
      /* usar saldo de la programación */
    }
  }
}
